import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Purchase } from "@libs/platform-wire/purchase";
import {
  accessesGetByIdQueryKey,
  useAccessesGetByIdQuery,
  type AccessRead,
} from "@hooks/trpc/useAccesses";
import { useData } from "../../../runtime/provider";

/**
 * The purchase an access offers, as its access read says it (LIS-1173):
 * the server's description of what the purchase sheet draws, compiled for
 * the fan, or `null` where it offers no listing. It is the access read's
 * own (`accesses.getById`), shared with every component showing the
 * access; no other read is made for it.
 *
 * - `purchase`: `undefined` while the access is read.
 * - `failed`: the read failed and nothing was read before; `retry` asks
 *   again.
 * - `update(purchase)`: the purchase as the sheet read it again for the
 *   buyer (sold out since), so the access's button follows it.
 */
export function useAccessPurchase(accessId: string | null) {
  const data = useData();
  const query = useQuery({
    ...useAccessesGetByIdQuery(data, accessId ?? ""),
    enabled: accessId !== null,
  });
  const access = accessId === null ? undefined : query.data;
  const purchase = access === undefined ? undefined : access.type === "music" ? null : access.purchase;
  const { refetch } = query;
  const retry = useCallback(() => void refetch(), [refetch]);
  const update = useCallback(
    (next: Purchase) => {
      if (accessId === null) return;
      data.queryClient.setQueryData<AccessRead>(accessesGetByIdQueryKey(accessId), (current) =>
        current && current.type !== "music" ? { ...current, purchase: next } : current,
      );
    },
    [accessId, data],
  );
  return {
    purchase,
    failed: purchase === undefined && query.isError && query.fetchStatus === "idle",
    retry,
    update,
  };
}
